import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Container, Group, Skeleton, Stack, Text } from '@mantine/core';
import PageTitle from '@/components/common/PageTitle';
import { IconPlus, IconSearch, IconWheat } from '@tabler/icons-react';
import EmptyState from '@/components/common/EmptyState';
import BuyFilterBar from '@/components/app/buy/BuyFilterBar';
import SellBasisCard from '@/components/app/sell/SellBasisCard';
import AgencyGateBanner from '@/components/app/sell/AgencyGateBanner';
import SellAttestationGate from '@/components/app/sell/SellAttestationGate';
import BestPriceAlertCard from '@/components/app/sell/BestPriceAlertCard';
import { useAgencyAgreementMissed, useSaleTree, useStartSellOffer } from '@/hooks/useSell';
import { useSaleBestOffersMap } from '@/hooks/useBestOffers';
import { basisKey, useBasisGoodsFilter } from '@/hooks/useBasisGoodsFilter';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import { useProfile } from '@/hooks/useProfile';
import { useRequiredData } from '@/hooks/useRequiredData';
import { useIsAgent } from '@/hooks/useRoles';
import { flattenSaleTree, groupSellByBasis } from '@/lib/sellTree';
import { isServiceUnavailable } from '@/api/errors';
import { ROUTES } from '@/routes/paths';
import { formatOrgName } from '@/lib/format';

// «Продам» — мои офферы на продажу: карточки базисов → строки-культуры → (раскрытие) офферы-лоты.
// Ограничение по агентскому договору касается только этого раздела (баннер и кнопка создания), «Купить» не трогает.
export default function SellListPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useSaleTree();
  const isAgent = useIsAgent(); // агент не публикует предложения о продаже (только посредничает)
  const agencyMissed = useAgencyAgreementMissed();
  const attestationRequired = useAttestationRequired();
  const startCreate = useStartSellOffer();
  const bestOffers = useSaleBestOffersMap();

  // Создавать предложение может только продавец (не агент) с пройденной аттестацией И подписанным
  // агентским договором. Если чего-то не хватает — кнопку не показываем вовсе (а не редиректим по клику):
  // нужный призыв к действию уже даёт баннер выше (SellAttestationGate / AgencyGateBanner).
  // Пока профиль и обязательные данные не загружены, оба ограничения считаются снятыми: кнопка мелькнула бы и исчезла.
  // Поэтому показываем её только после загрузки; сбой этих запросов кнопку не прячет (форма проверит ограничения сама).
  const profile = useProfile();
  const requiredData = useRequiredData();
  const gatesKnown = (profile.data != null || profile.isError) && (requiredData.data != null || requiredData.isError);
  const canCreate = gatesKnown && !isAgent && !attestationRequired && !agencyMissed;

  const groups = useMemo(() => flattenSaleTree(data), [data]);
  const allBases = useMemo(() => groupSellByBasis(groups), [groups]);
  const basisOptions = useMemo(
    () =>
      allBases.map((base) => ({
        value: basisKey(base),
        label: `${formatOrgName(base.addressTitle)} — ${base.address}`,
        title: base.addressTitle,
        address: base.address,
      })),
    [allBases],
  );
  const goodsOptions = useMemo(() => [...new Set(groups.map((group) => group.goods))].sort(), [groups]);
  // Фильтр записан в адрес страницы (после создания предложения и из «Лучшей цены» он приходит в ссылке).
  const { basis, good, setBasis, setGood, reset } = useBasisGoodsFilter(
    data ? basisOptions.map((option) => option.value) : null,
    data ? goodsOptions : null,
  );

  const filtered = useMemo(
    () => groups.filter((group) => (!basis || basisKey(group) === basis) && (!good || group.goods === good)),
    [groups, basis, good],
  );
  const basisCards = useMemo(() => groupSellByBasis(filtered), [filtered]);
  const showFilters = allBases.length > 1 || goodsOptions.length > 1;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* wrap: на узком экране кнопки действий переносятся под заголовок, а не сжимают его в столбик. */}
        <Group justify="space-between" wrap="wrap" gap="md" align="flex-start">
          <Stack gap={4} style={{ flex: '1 1 260px', minWidth: 0 }}>
            <PageTitle>{t('app.sell.title')}</PageTitle>
            <Text size="sm" c="gray.8">
              {t('app.sell.subtitle')}
            </Text>
          </Stack>
          <Group gap="sm" wrap="wrap" style={{ flexShrink: 0, maxWidth: '100%' }}>
            <Button component={Link} to={ROUTES.APP_SELL_BEST_PRICE} variant="default" leftSection={<IconSearch size={16} />}>
              {t('app.sell.bestPrice')}
            </Button>
            {groups.length > 0 && canCreate ? (
              <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>
                {t('app.sell.newOffer')}
              </Button>
            ) : null}
          </Group>
        </Group>

        {/* Аттестация — базовое требование, показываем её приоритетнее агентского договора. */}
        {attestationRequired && !isAgent ? <SellAttestationGate /> : agencyMissed ? <AgencyGateBanner /> : null}

        {!isAgent && !agencyMissed && groups.length > 0 ? <BestPriceAlertCard /> : null}

        {/* Ошибка во весь блок — только когда показывать нечего; при сбое обновления список остаётся, ошибка над ним. */}
        {isError ? (
          <Group gap="md">
            <Text c="loss.8">{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
            <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </Group>
        ) : null}

        {isError && !data ? null : isLoading ? (
          <Stack gap={8}>
            <Skeleton h={24} w={260} />
            <Skeleton h={36} />
            <Skeleton h={36} />
            <Skeleton h={36} />
          </Stack>
        ) : groups.length === 0 ? (
          isAgent ? (
            <EmptyState
              decorated={false}
              icon={<IconWheat size={30} />}
              title={t('app.sell.agentTitle')}
              description={t('app.sell.agentHint')}
            />
          ) : (
            <EmptyState
              decorated={false}
              icon={<IconWheat size={30} />}
              title={t('app.sell.empty')}
              description={t('app.sell.emptyHint')}
              action={
                canCreate ? (
                  <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>
                    {t('app.sell.create')}
                  </Button>
                ) : undefined
              }
            />
          )
        ) : (
          <Stack gap="md">
            {showFilters ? (
              <Group justify="space-between" wrap="wrap" align="center" gap="sm">
                <BuyFilterBar
                  bases={basisOptions}
                  goods={goodsOptions}
                  basis={basis}
                  good={good}
                  onBasis={(value) => {
                    const option = basisOptions.find((item) => item.value === value);
                    setBasis(option ? { address: option.address, addressTitle: option.title } : null);
                  }}
                  onGood={setGood}
                />
                <Text size="sm" c="gray.8">
                  {t('app.sell.bases', { count: basisCards.length })}
                </Text>
              </Group>
            ) : null}
            {basisCards.length === 0 ? (
              <Group gap="xs">
                <Text c="gray.7" size="sm">
                  {t('app.sell.nothingFound')}
                </Text>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={reset}
                >
                  {t('app.sell.resetFilters')}
                </Button>
              </Group>
            ) : (
              <Stack gap={34}>
                {basisCards.map((basisGroup) => (
                  <SellBasisCard key={basisKey(basisGroup)} basis={basisGroup} good={good} bestOffers={bestOffers} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
